/**
 * Demo module data for ?mock=1, so the phone layout can be seen with approvals, costs and
 * notifications before those modules run. Add &ext=none to see the honest empty states.
 */
import type { ApprovalRequest, ApprovalsState, NotifyState } from '@orgus/protocol';
import { MOCK_IDS } from '../../lib/mock.ts';
import { mockAsks } from '../approvals/mock.ts';

const MIN = 60_000;

export function demoApprovals(now: number): ApprovalsState {
  const base = (id: string, o: Partial<ApprovalRequest>): ApprovalRequest => ({
    id,
    kind: 'tool',
    machineId: MOCK_IDS.studio,
    sessionId: 'c3d8a7f1-request-card',
    projectId: MOCK_IDS.qawl,
    missionId: null,
    title: 'Run a command',
    risk: 'low',
    createdAt: now - 2 * MIN,
    expiresAt: now + 13 * MIN,
    state: 'pending',
    ...o,
  });
  return {
    pending: [
      base('apr_demo_land', {
        title: 'Run npm run land',
        tool: 'Bash',
        input: { command: 'npm run land -- --branch approvals-card' },
        reason: 'Pushes the verified approvals batch to main through the landing queue.',
        risk: 'high',
        policyHint: { decision: 'ask', ruleId: 'pol_push_main' },
        createdAt: now - 3 * MIN,
        expiresAt: now + 12 * MIN,
      }),
      base('apr_demo_test', {
        title: 'Run the webhook tests',
        tool: 'Bash',
        input: { command: 'npm test -- webhooks' },
        reason: 'Checks the retry change before opening the PR.',
        machineId: MOCK_IDS.mbp,
        sessionId: '9d1a4c6e-webhook-tests',
        projectId: MOCK_IDS.billing,
        risk: 'low',
        policyHint: { decision: 'allow' },
        createdAt: now - 1 * MIN,
        expiresAt: now + 14 * MIN,
      }),
      base('apr_demo_edit', {
        title: 'Edit src/server/auth.ts',
        tool: 'Edit',
        input: { file_path: 'src/server/auth.ts' },
        reason: 'Moves the org check ahead of the body parse.',
        risk: 'medium',
        createdAt: now - 4 * MIN,
        expiresAt: now + 2 * MIN,
      }),
      // Questions, a plan and MCP forms, answered in place (R-HL-6).
      ...mockAsks([], now).pending,
    ],
    recent: [
      base('apr_demo_old', { title: 'Run npm run typecheck', state: 'allowed', decidedBy: 'owner', decidedAt: now - 20 * MIN, scope: 'session', createdAt: now - 21 * MIN }),
    ],
  };
}

export function demoNotify(now: number): NotifyState {
  return {
    vapidPublicKey: null,
    channels: [],
    recent: [
      { id: 'ntf_demo_1', at: now - 3 * MIN, urgency: 'high', title: 'Approval needed in Qawl-Agents', body: 'Run npm run land. Pushes the verified approvals batch to main.', url: '/inbox?approval=apr_demo_land' },
      { id: 'ntf_demo_2', at: now - 9 * MIN, urgency: 'normal', title: 'Landing queue failed in billing-service', body: 'fix: retry webhooks with the event id', url: '/pipelines' },
      { id: 'ntf_demo_3', at: now - 41 * MIN, urgency: 'high', title: 'Budget at 81%', body: '$65.05 of $80.00 spent. Forecast for the period: $96.00.', url: '/spend' },
    ],
  };
}
